interface PreviewMediaCapture {
  getVideoTrack(): MediaStreamTrack | null;
  startVideoCapture(deviceId: string): Promise<MediaStreamTrack | null>;
  startShadowcamCapture(name: string): Promise<MediaStreamTrack | null>;
  restartShadowcamCapture(name: string): Promise<MediaStreamTrack | null>;
}

/** Keeps Studio preview on the selected source instead of replacing webcam capture with ShadowCam. */
export class PreviewCaptureSource {
  constructor(private readonly media: PreviewMediaCapture) {}

  public isShadowcamSource(deviceId: string): boolean {
    return ['display', 'followMouse', 'gameCapture'].includes(deviceId);
  }

  public matches(track: MediaStreamTrack | null, deviceId: string, shadowcam: string): boolean {
    if (!track || track.readyState !== 'live') return false;
    return this.isShadowcamSource(deviceId)
      ? track.label.includes(shadowcam)
      : track.getSettings().deviceId === deviceId;
  }

  public async capture(deviceId: string, shadowcam: string): Promise<MediaStreamTrack | null> {
    if (!deviceId || deviceId === '0') return null;
    const current = this.media.getVideoTrack();
    if (this.matches(current, deviceId, shadowcam)) return current;
    return this.isShadowcamSource(deviceId)
      ? this.media.startShadowcamCapture(shadowcam)
      : this.media.startVideoCapture(deviceId);
  }

  /** Green-frame recovery applies to virtual capture only, never a physical webcam. */
  public async recover(deviceId: string, shadowcam: string): Promise<MediaStreamTrack | null> {
    if (!this.isShadowcamSource(deviceId)) return null;
    return this.media.restartShadowcamCapture(shadowcam);
  }
}
